import { MenuModel, RouteCodeEnum } from '@models/menu';

export type CipoTheme = 'default' | 'orange';
export type CipoThemeMode = 'dark' | 'light' | 'browser';
export type Theme = {
  color: CipoTheme;
  mode: CipoThemeMode;
};

export type A11yContrast = 'normal' | 'high' | 'invert';
export type A11yFontScale = 'normal' | 'lg' | 'xl';
export type A11yTextSpacing = 'normal' | 'comfortable' | 'spacious';
export type A11yLineHeight = 'normal' | 'comfortable' | 'spacious';
export type A11ySaturation = 'normal' | 'low' | 'high' | 'desaturate';
export type A11yColorFilter = 'normal' | 'protanopia' | 'deuteranopia' | 'tritanopia';
export type A11yReadingAid = 'off' | 'guide' | 'mask';
export type A11yPreferences = {
  contrast: A11yContrast;
  reduceMotion: boolean;
  fontScale: A11yFontScale;
  textSpacing: A11yTextSpacing;
  lineHeight: A11yLineHeight;
  saturation: A11ySaturation;
  underlineLinks: boolean;
  dyslexiaFont: boolean;
  colorFilter: A11yColorFilter;
  largeCursor: boolean;
  readingAid: A11yReadingAid;
};

// This is still needed, because the overlay is outside the app-root, so the classes don't get applied
export function appendClassesToBody({ theme, mode }: { theme: CipoTheme; mode: CipoThemeMode }) {
  const classesToRemove = Array.from(document.body.classList);

  // Remove existent classes
  classesToRemove.forEach(className => {
    if (className.endsWith('-theme') || className.endsWith('-mode')) {
      document.body.classList.remove(className);
    }
  });

  const themeMode = mode === 'browser' ? getBrowserThemeMode() : mode;

  document.body.classList.add(theme + '-theme', themeMode + '-mode');
}

function getBrowserThemeMode(): CipoThemeMode {
  const darkModeOn = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return darkModeOn ? 'dark' : 'light';
}

// Applied to <body> (not app-root) so CDK overlays also receive a11y classes.
// `colorFilter` and `readingAid` are excluded: they are owned by dedicated
// components that handle the absolute-URL SVG filter and pointer overlay directly.
export function appendA11yClassesToBody({
  contrast,
  reduceMotion,
  fontScale,
  textSpacing,
  lineHeight,
  saturation,
  underlineLinks,
  dyslexiaFont,
  largeCursor,
}: A11yPreferences) {
  Array.from(document.body.classList)
    .filter(className => className.startsWith('a11y-'))
    .forEach(className => document.body.classList.remove(className));

  if (contrast !== 'normal') {
    document.body.classList.add(`a11y-contrast-${contrast}`);
  }
  if (reduceMotion) {
    document.body.classList.add('a11y-reduce-motion');
  }
  if (fontScale !== 'normal') {
    document.body.classList.add(`a11y-font-${fontScale}`);
  }
  if (textSpacing !== 'normal') {
    document.body.classList.add(`a11y-text-spacing-${textSpacing}`);
  }
  if (lineHeight !== 'normal') {
    document.body.classList.add(`a11y-line-height-${lineHeight}`);
  }
  if (saturation !== 'normal') {
    document.body.classList.add(`a11y-saturation-${saturation}`);
  }
  if (underlineLinks) {
    document.body.classList.add('a11y-underline-links');
  }
  if (dyslexiaFont) {
    document.body.classList.add('a11y-dyslexia-font');
  }
  if (largeCursor) {
    document.body.classList.add('a11y-large-cursor');
  }
}

export function getMenuItems(menuItems: MenuModel[]): MenuModel[] {
  return menuItems.reduce((acc: MenuModel[], item) => {
    acc.push(item);
    if (item.children) {
      acc.push(...getMenuItems(item.children));
    }

    return acc;
  }, []);
}

export function findMenuItemByCode(menuItems: MenuModel[], code: RouteCodeEnum): MenuModel | undefined {
  for (const item of menuItems) {
    if (item.code === code) {
      return item;
    }
  }
  return undefined;
}
